import { mapResponseError } from '@mybusinessmate/api-client';
import { BRIEF_ASSET_MAX, type BriefAsset } from '@mybusinessmate/schema';
import { api } from './api';

/** Mirrors the API's `brief` upload rule (apps/api storage-policy.ts): the API re-checks, this only saves a round trip. */
export const BRIEF_UPLOAD_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'application/pdf',
] as const;
export const BRIEF_UPLOAD_MAX_BYTES = 200 * 1024 * 1024;
export { BRIEF_ASSET_MAX };

const MB = 1024 * 1024;

/**
 * A reason a file cannot be added, or null when it can. The wording is Figma P01's and is shown on the file's own
 * row, so it never repeats the file's name.
 */
function checkFile(file: Pick<File, 'type' | 'size'>, types: readonly string[], maxBytes: number): string | null {
  if (!types.includes(file.type)) return 'This file type isn’t allowed.';
  if (file.size === 0) return 'This file is empty.';
  if (file.size > maxBytes) return `${Math.ceil(file.size / MB)} MB is too big. Max ${Math.round(maxBytes / MB)} MB.`;
  return null;
}

export function checkBriefFile(file: Pick<File, 'type' | 'size' | 'name'>): string | null {
  return checkFile(file, BRIEF_UPLOAD_TYPES, BRIEF_UPLOAD_MAX_BYTES);
}

/** Mirrors the API's staff-only `deliverable` rule: what the team hands back, including a site's source zip. */
export const DELIVERABLE_UPLOAD_TYPES = [...BRIEF_UPLOAD_TYPES, 'application/zip'] as const;
export const DELIVERABLE_UPLOAD_MAX_BYTES = 500 * 1024 * 1024;

export function checkDeliverableFile(file: Pick<File, 'type' | 'size' | 'name'>): string | null {
  return checkFile(file, DELIVERABLE_UPLOAD_TYPES, DELIVERABLE_UPLOAD_MAX_BYTES);
}

export interface UploadHandle {
  /** Resolves with the stored object's key. Rejects on failure, or with `AbortError` when cancelled. */
  promise: Promise<string>;
  abort: () => void;
}

/** Asks the API for a presigned URL, then PUTs the file to it with progress (XHR: `fetch` has no upload progress). */
export function uploadBriefFile(file: File, businessId: string, onProgress: (fraction: number) => void): UploadHandle {
  return uploadFile(file, { businessId, category: 'brief' }, onProgress);
}

/** The same upload for what the team hands back on a task: kept under the task, staff only. */
export const uploadDeliverableFile =
  (taskId: string) =>
  (file: File, businessId: string, onProgress: (fraction: number) => void): UploadHandle =>
    uploadFile(file, { businessId, category: 'deliverable', taskId }, onProgress);

/** A file for the account vault: kept under the business, not a task (Figma A05.11b "File"). */
export function uploadVaultFile(file: File, businessId: string, onProgress: (fraction: number) => void): UploadHandle {
  return uploadFile(file, { businessId, category: 'deliverable' }, onProgress);
}

function uploadFile(
  file: File,
  where: { businessId: string; category: 'brief' | 'deliverable'; taskId?: string },
  onProgress: (fraction: number) => void,
): UploadHandle {
  const xhr = new XMLHttpRequest();
  let aborted = false;

  const promise = (async () => {
    const res = await api.api.storage['presigned-upload'].$post({
      json: {
        filename: file.name,
        contentType: file.type,
        contentLength: file.size,
        category: where.category,
        businessId: where.businessId,
        ...(where.taskId ? { taskId: where.taskId } : {}),
      },
    });
    if (!res.ok) throw await mapResponseError(res);
    const { uploadUrl, key } = await res.json();
    if (!uploadUrl) throw new Error('No upload URL');
    if (aborted) throw new DOMException('Upload cancelled', 'AbortError');

    await new Promise<void>((resolve, reject) => {
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection.'));
      xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
      xhr.send(file);
    });
    return key;
  })();

  return {
    promise,
    abort: () => {
      aborted = true;
      xhr.abort();
    },
  };
}

export const assetFromFile = (file: Pick<File, 'name' | 'size' | 'type'>, key: string): BriefAsset => ({
  id: crypto.randomUUID(),
  name: file.name,
  key,
  size: file.size,
  contentType: file.type,
});
